// src/pages/CompaniesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { companiesAPI } from '../services/api';
import { Card, Badge, SectionHeader, EmptyState, Button, Modal, Input, Select, Textarea, ConfirmModal } from '../components/ui';
import toast from 'react-hot-toast';
import { Building2, Plus, Pencil, Globe, Mail, Phone, Trash2, Search } from 'lucide-react';
import styles from './CompaniesPage.module.css';

const INDUSTRIES = ['Tobacco', 'Coffee', 'Agriculture', 'Food_Beverage', 'Manufacturing', 'Logistics', 'Retail', 'Pharma', 'Other'];
const TIMEZONES = [
    { value: 'America/New_York', label: 'US Eastern (GMT-5)' },
    { value: 'America/Tegucigalpa', label: 'Honduras (GMT-6)' },
    { value: 'America/Guatemala', label: 'Guatemala (GMT-6)' },
    { value: 'America/El_Salvador', label: 'El Salvador (GMT-6)' },
    { value: 'America/Managua', label: 'Nicaragua (GMT-6)' },
    { value: 'America/Costa_Rica', label: 'Costa Rica (GMT-6)' },
    { value: 'America/Panama', label: 'Panama (GMT-5)' },
    { value: 'America/Bogota', label: 'Colombia (GMT-5)' },
    { value: 'UTC', label: 'UTC' },
];

export default function CompaniesPage() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await companiesAPI.list();
            setCompanies((res.data.companies || []).filter(c => c.status == null || c.status === 'active'));
        } catch (err) { toast.error('Failed to Load Companies'); }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await companiesAPI.update(deleteTarget.companyId, { status: 'inactive' });
            toast.success('Company Deactivated');
            setDeleteTarget(null);
            load();
        } catch (err) { toast.error(err?.response?.data?.message || 'Failed to Deactivate Company'); }
        setDeleteLoading(false);
    };

    const filtered = companies.filter(c =>
        !search ||
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.industry?.toLowerCase().includes(search.toLowerCase()) ||
        c.city?.toLowerCase().includes(search.toLowerCase()) ||
        c.country?.toLowerCase().includes(search.toLowerCase()) ||
        c.contactEmail?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Companies"
                description="Manage GAAFrfid Client Companies — GAAF Admin only"
                actions={<Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>Add Company</Button>}
            />

            <div style={{ position: 'relative', maxWidth: 360, marginBottom: 16 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by Name, Industry, City, Email…"
                    style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>

            {loading ? (
                <div className={styles.grid}>{[1, 2, 3].map(i => <div key={i} className={styles.skeleton} />)}</div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<Building2 size={40} />}
                    title={search ? 'No Companies match your search' : 'No Companies yet'}
                    description={search ? 'Try a different Name, Industry, or City.' : 'Add your first Client Company to get started.'}
                    action={!search && <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>Add Company</Button>}
                />
            ) : (
                <div className={styles.grid}>
                    {filtered.map(c => (
                        <CompanyCard key={c.companyId} company={c} onEdit={() => setEditing(c)} onDelete={() => setDeleteTarget(c)} />
                    ))}
                </div>
            )}

            <ConfirmModal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleteLoading}
                title="Deactivate Company" message={`Deactivate "${deleteTarget?.name}"? All their data is preserved but they will no longer appear in the platform.`} confirmLabel="Deactivate" />
            <CompanyModal open={showCreate || !!editing} company={editing}
                onClose={() => { setShowCreate(false); setEditing(null); }}
                onSaved={() => { setShowCreate(false); setEditing(null); load(); }} />
        </div>
    );
}

function CompanyCard({ company, onEdit, onDelete }) {
    const alertEmails = company.alertEmails && typeof company.alertEmails === 'object' && !Array.isArray(company.alertEmails) ? company.alertEmails : {};
    const alertCount = Object.values(alertEmails).flat().length;
    return (
        <Card className={styles.companyCard} hover>
            <div className={styles.companyHeader}>
                <div className={styles.companyLogo}>
                    {company.logoUrl
                        ? <img src={company.logoUrl} alt={company.name} className={styles.logoImg} />
                        : <span className={styles.logoLetter}>{(company.name || '?')[0].toUpperCase()}</span>
                    }
                </div>
                <div className={styles.companyInfo}>
                    <h3 className={styles.companyName}>{company.name || '—'}</h3>
                    <Badge variant={company.status === 'active' ? 'success' : 'neutral'} size="sm">{company.status || 'active'}</Badge>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                    <button className={styles.editBtn} onClick={onEdit} title="Edit Company"><Pencil size={14} /></button>
                    <button className={styles.editBtn} onClick={onDelete} title="Deactivate Company" style={{ color: '#e07a7a' }}><Trash2 size={14} /></button>
                </div>
            </div>
            <div className={styles.companyDetails}>
                {company.industry && <div className={styles.detail}><Globe size={12} /><span>{company.industry}</span></div>}
                {company.contactEmail && <div className={styles.detail}><Mail size={12} /><span>{company.contactEmail}</span></div>}
                {company.phone && <div className={styles.detail}><Phone size={12} /><span>{company.phone}</span></div>}
                {(company.city || company.country) && <div className={styles.detail}><span>📍</span><span>{[company.city, company.country].filter(Boolean).join(', ')}</span></div>}
            </div>
            <div className={styles.companyMeta}>
                <div className={styles.metaItem}><span className={styles.metaLabel}>EPC Prefix</span><code className={styles.metaValue}>{company.epcPrefix}</code></div>
                <div className={styles.metaItem}><span className={styles.metaLabel}>Alert Emails</span><span className={styles.metaValue}>{alertCount} configured</span></div>
                <div className={styles.metaItem}><span className={styles.metaLabel}>Language</span><span className={styles.metaValue}>{company.settings?.language || 'es'}</span></div>
                <div className={styles.metaItem}><span className={styles.metaLabel}>Timezone</span><span className={styles.metaValue}>{company.settings?.timezone || 'America/Tegucigalpa'}</span></div>
                <div className={styles.metaItem}><span className={styles.metaLabel}>Low Stock Threshold</span><span className={styles.metaValue}>{company.settings?.defaultLowStockThreshold || 0}</span></div>
            </div>
            <div className={styles.companyId}>
                <span className={styles.metaLabel}>Company ID</span>
                <code style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{company.companyId}</code>
            </div>
        </Card>
    );
}

function CompanyModal({ open, company, onClose, onSaved }) {
    const [form, setForm] = useState({ name: '', industry: 'Tobacco', contactName: '', contactEmail: '', phone: '', country: 'Honduras', city: '', epcPrefix: '', lowStockThreshold: 10, language: 'es', timezone: 'America/Tegucigalpa', purchasesEmail: '' });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        if (company) {
            setForm({
                name: company.name || '', industry: company.industry || 'Tobacco',
                contactName: company.contactName || '', contactEmail: company.contactEmail || '',
                phone: company.phone || '', country: company.country || 'Honduras',
                city: company.city || '', epcPrefix: company.epcPrefix || '',
                lowStockThreshold: company.settings?.defaultLowStockThreshold || 10,
                language: company.settings?.language || 'es',
                timezone: company.settings?.timezone || 'America/Tegucigalpa',
                purchasesEmail: (company.alertEmails?.lowStock || [])[0] || '',
            });
        } else {
            setForm({ name: '', industry: 'Tobacco', contactName: '', contactEmail: '', phone: '', country: 'Honduras', city: '', epcPrefix: '', lowStockThreshold: 10, language: 'es', timezone: 'America/Tegucigalpa', purchasesEmail: '' });
        }
    }, [company, open]);

    const handleSave = async () => {
        if (!form.name) return toast.error('Company Name is Required');
        if (!company && (!form.epcPrefix || !/^[0-9A-Fa-f]{6}$/.test(form.epcPrefix))) return toast.error('EPC Prefix must be exactly 6 hex characters (e.g. A1B2C3)');
        setLoading(true);
        try {
            const payload = {
                name: form.name, industry: form.industry, contactName: form.contactName, contactEmail: form.contactEmail,
                phone: form.phone, country: form.country, city: form.city,
                ...(!company ? { epcPrefix: form.epcPrefix.toUpperCase() } : {}),
                alertEmails: { lowStock: form.purchasesEmail ? [form.purchasesEmail] : [], anomaly: [], general: form.contactEmail ? [form.contactEmail] : [] },
                settings: { defaultLowStockThreshold: parseInt(form.lowStockThreshold) || 10, language: form.language, timezone: form.timezone },
            };
            if (company) { await companiesAPI.update(company.companyId, payload); toast.success('Company Updated'); }
            else { await companiesAPI.create(payload); toast.success('Company Created'); }
            onSaved();
        } catch (err) { toast.error(err?.response?.data?.message || 'Failed to Save Company'); }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} title={company ? `Edit — ${company.name || 'Company'}` : 'Add Company'} size="lg">
            <div className={styles.formGrid}>
                <Input label="Company Name *" value={form.name} onChange={e => set('name', e.target.value)} placeholder="GAAF Cigars" />
                <Select label="Industry *" value={form.industry} onChange={e => set('industry', e.target.value)}>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i.replace('_', ' ')}</option>)}
                </Select>
                <Input label="Contact Name" value={form.contactName} onChange={e => set('contactName', e.target.value)} placeholder="Joe Doe" />
                <Input label="Contact Email" type="email" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} placeholder="joe@company.com" />
                <Input label="Phone" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+123456789" />
                <Input label="City" value={form.city} onChange={e => set('city', e.target.value)} placeholder="Jalapa" />
                <Input label="Country" value={form.country} onChange={e => set('country', e.target.value)} />
                {!company && (
                    <Input label="EPC Prefix * (6 hex chars)" value={form.epcPrefix} onChange={e => set('epcPrefix', e.target.value.toUpperCase())}
                        placeholder="A1B2C3" hint="Unique Company Identifier for RFID Tag Encoding. Cannot be changed later." maxLength={6} />
                )}
                <Input label="Purchases Department Email (Low Stock Alerts)" type="email" value={form.purchasesEmail} onChange={e => set('purchasesEmail', e.target.value)} placeholder="purchases@company.com" />
                <Input label="Default Low Stock Threshold" type="number" min="0" value={form.lowStockThreshold} onChange={e => set('lowStockThreshold', e.target.value)} />
                <Select label="Dashboard Language" value={form.language} onChange={e => set('language', e.target.value)}>
                    <option value="es">Español</option>
                    <option value="en">English</option>
                    <option value="pt">Português</option>
                </Select>
                <Select label="Timezone" value={form.timezone} onChange={e => set('timezone', e.target.value)}>
                    {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                </Select>
                <div className={`${styles.fullSpan} ${styles.modalActions}`}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" loading={loading} onClick={handleSave}>{company ? 'Save Changes' : 'Create Company'}</Button>
                </div>
            </div>
        </Modal>
    );
}
